/**
 * Utility functions for student enrollment state transitions.
 */

const TERMINAL_STATES = ['WITHDRAWN', 'DENIED'];
const ACCEPTABLE_STATES = ['PENDING_ADMISSIONS_REVIEW', 'WAITLISTED'];
const WAITLISTABLE_STATES = ['PENDING_ADMISSIONS_REVIEW'];
const DENIABLE_STATES = ['PENDING_ADMISSIONS_REVIEW', 'WAITLISTED'];

export interface StudentEnrollmentActions {
  canAccept: boolean;
  canWaitlist: boolean;
  canDeny: boolean;
  canWithdraw: boolean;
}

export function getAvailableStudentEnrollmentActions(state: string): StudentEnrollmentActions {
  return {
    canAccept: ACCEPTABLE_STATES.includes(state),
    canWaitlist: WAITLISTABLE_STATES.includes(state),
    canDeny: DENIABLE_STATES.includes(state),
    canWithdraw: !TERMINAL_STATES.includes(state),
  };
}

export function hasAvailableActions(state: string): boolean {
  const actions = getAvailableStudentEnrollmentActions(state);
  return actions.canAccept || actions.canWaitlist || actions.canDeny || actions.canWithdraw;
}

export const ACTIONS_REQUIRING_CONFIRMATION = ['deny', 'withdraw'];

interface ActionConfirmation {
  title: string;
  description: string;
  confirmLabel: string;
  variant: string;
}

export function getActionConfirmation(action: string, studentName: string): ActionConfirmation | null {
  const confirmations: Record<string, ActionConfirmation> = {
    deny: {
      title: 'Deny Student Enrollment',
      description: `Are you sure you want to deny ${studentName}'s enrollment? This action cannot be undone.`,
      confirmLabel: 'Deny Enrollment',
      variant: 'destructive',
    },
    withdraw: {
      title: 'Withdraw Student',
      description: `Are you sure you want to withdraw ${studentName}? This will remove them from the enrollment.`,
      confirmLabel: 'Withdraw Student',
      variant: 'destructive',
    },
  };

  return confirmations[action] || null;
}
